function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  const workspaceId = localStorage.getItem('active_workspace_id');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (workspaceId) headers['X-Workspace-ID'] = workspaceId;
  return headers;
}

/**
 * Streams an SSE response from a POST endpoint.
 * Expected event format:
 *   data: {"chunk": "..."}  ← text chunk
 *   data: [DONE]            ← stream complete
 */
export async function streamSSE(
  url: string,
  body: object,
  onChunk: (chunk: string) => void,
  onDone: () => void,
  onError: (err: Error) => void
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
      // Match the axios client's `withCredentials: true` so the
      // backend-issued `prodagi_session` cookie lands in the jar on
      // these authenticated SSE calls too.
      credentials: 'include',
    });
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!response.ok) {
    onError(new Error(`HTTP ${response.status}`));
    return;
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            onDone();
            return;
          }
          try {
            const parsed = JSON.parse(data);
            if (parsed.chunk) {
              onChunk(parsed.chunk);
            } else {
              // Canary for contract drift (audit fcb6e77). The documented
              // wire-format is `data: {"chunk":"..."}`. If the JSON parses
              // but lacks a `chunk` key we still drop the frame (backward-
              // compatible behavior) — but loud-warn so the next backend
              // SSE-shape regression fails visibly in devtools instead of
              // silently turning the AI preview blank.
              const keys = Object.keys(parsed || {}).join(', ');
              console.warn(
                '[streamSSE] unrecognized SSE frame shape — expected {"chunk":...}, got keys:',
                keys || '<empty>',
                'sample:', data.slice(0, 100),
              );
            }
          } catch {
            // Non-JSON chunk — use raw data
            onChunk(data);
          }
        }
      }
    }
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  onDone();
}
